import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Link as RouterLink, Navigate } from 'react-router';
import { Box, Button, Typography } from '@mui/material';
import { Permission } from '@pd/shared';
import { useAuth } from '../auth/AuthContext';
import { FullPageLoader, GuestOnly, RequireAuth, RequirePermission } from '../auth/guards';
import { AppLayout } from '../components/AppLayout';
import { RouteError } from '../components/RouteError';
import { ChangePasswordPage } from '../features/auth/ChangePasswordPage';
import { ForgotPasswordPage } from '../features/auth/ForgotPasswordPage';
import { LoginPage } from '../features/auth/LoginPage';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage';
import { t } from '../i18n/sq';

// Heavy screens (calendar, rich-text editor, date pickers) are code-split per route.
const page = <T extends Record<string, ComponentType>>(load: () => Promise<T>, name: keyof T) =>
  lazy(() => load().then((m) => ({ default: m[name] as ComponentType })));
const ActivityDetailPageLazy = page(
  () => import('../features/activities/ActivityDetailPage'),
  'ActivityDetailPage',
);
const ActivityFormPageLazy = page(
  () => import('../features/activities/ActivityFormPage'),
  'ActivityFormPage',
);
const ActivityListPageLazy = page(
  () => import('../features/activities/ActivityListPage'),
  'ActivityListPage',
);
const CalendarPageLazy = page(() => import('../features/activities/CalendarPage'), 'CalendarPage');
const AuditPageLazy = page(() => import('../features/audit/AuditPage'), 'AuditPage');
const DashboardPageLazy = page(
  () => import('../features/dashboard/DashboardPage'),
  'DashboardPage',
);
const StructurePageLazy = page(
  () => import('../features/structure/StructurePage'),
  'StructurePage',
);
const CategoriesPageLazy = page(
  () => import('../features/categories/CategoriesPage'),
  'CategoriesPage',
);
const ProfilePageLazy = page(() => import('../features/profile/ProfilePage'), 'ProfilePage');
const SettingsPageLazy = page(() => import('../features/settings/SettingsPage'), 'SettingsPage');
const PersonPageLazy = page(() => import('../features/people/PersonPage'), 'PersonPage');
const NotificationsPageLazy = page(
  () => import('../features/notifications/NotificationsPage'),
  'NotificationsPage',
);
const AnnouncementsPageLazy = page(
  () => import('../features/announcements/AnnouncementsPage'),
  'AnnouncementsPage',
);
const AnnouncementFormPageLazy = page(
  () => import('../features/announcements/AnnouncementFormPage'),
  'AnnouncementFormPage',
);
const AnnouncementPageLazy = page(
  () => import('../features/announcements/AnnouncementPage'),
  'AnnouncementPage',
);
const ActivityReportPageLazy = page(
  () => import('../features/activities/ActivityReportPage'),
  'ActivityReportPage',
);
const ConcernsPageLazy = page(() => import('../features/concerns/ConcernsPage'), 'ConcernsPage');
const ConcernTopicsPageLazy = page(
  () => import('../features/concerns/ConcernTopicsPage'),
  'ConcernTopicsPage',
);
const MessagesPageLazy = page(() => import('../features/messages/MessagesPage'), 'MessagesPage');
const UsersPageLazy = page(() => import('../features/users/UsersPage'), 'UsersPage');

/** Leadership lands on the dashboard; people who post activities land on their calendar. */
function Home() {
  const { can } = useAuth();
  return <Navigate to={can(Permission.DASHBOARD_VIEW) ? '/paneli' : '/kalendari'} replace />;
}

function NotFound() {
  return (
    <Box sx={{ textAlign: 'center', py: 10 }}>
      <Typography variant="h4" gutterBottom>
        {t.notFound.title}
      </Typography>
      <Button component={RouterLink} to="/" variant="contained">
        {t.notFound.back}
      </Button>
    </Box>
  );
}

// URL slugs are Albanian, matching the product language (see docs/10-ui-guidelines.md).
export const router = createBrowserRouter([
  {
    element: <GuestOnly />,
    errorElement: <RouteError />,
    children: [
      { path: '/hyr', element: <LoginPage /> },
      { path: '/harruat-fjalekalimin', element: <ForgotPasswordPage /> },
    ],
  },
  // Reachable signed-in or not: invitation links may be opened in any browser.
  { path: '/rivendos-fjalekalimin', element: <ResetPasswordPage />, errorElement: <RouteError /> },
  {
    element: <RequireAuth />,
    errorElement: <RouteError />,
    children: [
      {
        element: (
          <Suspense fallback={<FullPageLoader />}>
            <AppLayout />
          </Suspense>
        ),
        children: [
          { index: true, element: <Home /> },
          { path: '/profili', element: <ProfilePageLazy /> },
          { path: '/cilesimet/:section?', element: <SettingsPageLazy /> },
          // Forced first-login change; otherwise redirects to Settings like the old devices link.
          { path: '/ndrysho-fjalekalimin', element: <ChangePasswordPage /> },
          { path: '/pajisjet', element: <Navigate to="/cilesimet/siguria" replace /> },
          { path: '/njerez/:id', element: <PersonPageLazy /> },
          { path: '/njoftimet', element: <NotificationsPageLazy /> },
          {
            path: '/lajmerimet',
            element: (
              <RequirePermission permission={Permission.ANNOUNCEMENT_SEND}>
                <AnnouncementsPageLazy />
              </RequirePermission>
            ),
          },
          {
            path: '/lajmerimet/i-ri',
            element: (
              <RequirePermission permission={Permission.ANNOUNCEMENT_SEND}>
                <AnnouncementFormPageLazy />
              </RequirePermission>
            ),
          },
          // Recipients open it from the bell or the e-mail; the API decides who may read it.
          { path: '/lajmerimet/:id', element: <AnnouncementPageLazy /> },
          {
            path: '/paneli',
            element: (
              <RequirePermission permission={Permission.DASHBOARD_VIEW}>
                <DashboardPageLazy />
              </RequirePermission>
            ),
          },
          { path: '/kalendari', element: <CalendarPageLazy /> },
          { path: '/aktivitetet', element: <ActivityListPageLazy /> },
          {
            path: '/aktivitetet/i-ri',
            element: (
              <RequirePermission permission={Permission.ACTIVITY_CREATE}>
                <ActivityFormPageLazy />
              </RequirePermission>
            ),
          },
          { path: '/aktivitetet/:id', element: <ActivityDetailPageLazy /> },
          {
            path: '/aktivitetet/:id/ndrysho',
            element: (
              <RequirePermission permission={Permission.ACTIVITY_UPDATE_OWN}>
                <ActivityFormPageLazy />
              </RequirePermission>
            ),
          },
          {
            path: '/aktivitetet/:id/raporti',
            element: (
              <RequirePermission permission={Permission.ACTIVITY_UPDATE_OWN}>
                <ActivityReportPageLazy />
              </RequirePermission>
            ),
          },
          { path: '/mesazhet', element: <MessagesPageLazy /> },
          {
            path: '/shqetesimet',
            element: (
              <RequirePermission permission={Permission.DASHBOARD_VIEW}>
                <ConcernsPageLazy />
              </RequirePermission>
            ),
          },
          {
            path: '/temat',
            element: (
              <RequirePermission permission={Permission.REFERENCE_DATA_MANAGE}>
                <ConcernTopicsPageLazy />
              </RequirePermission>
            ),
          },
          {
            path: '/perdoruesit',
            element: (
              <RequirePermission permission={Permission.USER_MANAGE}>
                <UsersPageLazy />
              </RequirePermission>
            ),
          },
          {
            path: '/struktura',
            element: (
              <RequirePermission permission={Permission.REFERENCE_DATA_MANAGE}>
                <StructurePageLazy />
              </RequirePermission>
            ),
          },
          {
            path: '/kategorite',
            element: (
              <RequirePermission permission={Permission.REFERENCE_DATA_MANAGE}>
                <CategoriesPageLazy />
              </RequirePermission>
            ),
          },
          {
            path: '/auditimi',
            element: (
              <RequirePermission permission={Permission.AUDIT_VIEW}>
                <AuditPageLazy />
              </RequirePermission>
            ),
          },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);
